<script lang="ts" setup>
import { type FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { PrimeVuePt } from '@shared/plugin/prime-vue';
import Chip, { type ChipProps } from 'primevue/chip';
import { computed } from 'vue';
import { type BasicColor } from '@tailwind-preset/colors';

export type VChipProps = {
  color?: keyof typeof colors;
  hideIcon?: keyof typeof hideIconBreakpoints;
  icon?: FontAwesomeIconProps['icon'];
  label?: ChipProps['label'];
  pending?: boolean;
  size?: keyof typeof sizes;
};

const props = withDefaults(defineProps<VChipProps>(), { size: 'base' });

const colors: Record<BasicColor, string> = {
  blue: 'bg-blue',
  orange: 'bg-orange',
  red: 'bg-red',
  teal: 'bg-teal',
  grey: 'bg-grey-300',
  green: 'bg-green-700',
} as const;

const hideIconBreakpoints = {
  sm: 'max-sm:hidden',
  md: 'max-md:hidden',
  lg: 'max-lg:hidden',
  xl: 'max-xl:hidden',
  '2xl': 'max-2xl:hidden',
} as const;

const sizes = {
  sm: 'text-[9px] px-1.5 leading-3 py-0.5',
  base: 'text-2xs px-2 leading-4 py-1',
  lg: 'text-[13px] px-3.5 leading-5 py-1.5',
} as const;

const colorClasses = computed(() => (props.color ? colors[props.color] : 'bg-grey-400'));
const componentProps = computed(() =>
  Object.fromEntries(Object.entries(props).filter(([key]) => key !== 'icon')),
);
const hideIconClasses = computed(() => (props.hideIcon ? hideIconBreakpoints[props.hideIcon] : ''));
const sizeClasses = computed(() => sizes[props.size]);

const displayIcon = computed(() =>
  props.pending ? (['fad', 'spinner-third'] as FontAwesomeIconProps['icon']) : props.icon,
);
</script>

<template>
  <Chip v-bind="componentProps" :class="[colorClasses, sizeClasses]">
    <template #icon>
      <FontAwesomeIcon
        v-if="displayIcon"
        :class="{ [hideIconClasses]: true, [PrimeVuePt.chip.icon.class]: true }"
        :icon="displayIcon"
        :spin="props.pending"
      />
    </template>
  </Chip>
</template>
